/**
 * 私有网 —— 真数据(hub 的 /net/health + /net/nodes + 设备注册表)。
 *
 * 这一版是照着几条成熟做法重做的,每条都对应旧版一个具体毛病:
 *
 * ① **小规模舰队,列表比图更有用**(Tailscale 的 machines 页就是列表 + 筛选,
 *    图是社区插件)。两三个节点的拓扑图信息量近乎为零,却占了整屏。
 *    → 拓扑/列表双视图,列表**不点也能看全**(名称/标识/IP/最后在线/接入方式)。
 * ② **radial 布局要以 hub 为组织中心, 用曲线边**(yFiles/Selector 的 radial 规范)。
 *    旧版固定 460 高、起始角 -90°,两个节点就退化成一条竖直线段 + 大片空白。
 *    → 画布高度随节点数收缩;n≤3 走水平扇形;边改二次贝塞尔。
 * ③ **KPI hero row 的每一格都要是"北极星"**(fleet dashboard 通例)。
 *    旧版四格里有一格是"链路延迟 —(无源)" —— 拿最显眼的位置说"我没有这个数"。
 *    → 换成真能驱动决策的「最久未上线」;无源那条降级成脚注。
 * ④ **节点要能一眼认出是什么设备**。旧版圆圈里印的是 IP 末位数字(1 / 2)。
 *    → 圆圈里放设备类型图标,名字用注册表里的中文名(dog-01 → 小智)。
 * ⑤ **两种接入方式画在同一张图上**。wg 对端(人的电脑)原来藏在右栏一个小卡里,
 *    看图的人根本不知道还有这类接入。→ 虚线接入 hub,形状与颜色都不同。
 *
 * 没变的是判据本身:控制面知道的只有"谁入网了、此刻在不在线、overlay IP 是多少"。
 * 连线是**注册关系不是实测链路**,链路质量依旧没有数据源 —— 那一条写在图例里,
 * 不因为界面变好看就悄悄升格成"实时链路图"。
 */
import { useMemo, useState } from 'react';
import { useNetwork, useTerminals } from '@/hooks/useHubData';
import { NoDataSource, LoadError } from '@/components/feature/DataSource';
import { timeAgo, timeAgoSec, NA } from '@/lib/format';
import type { HubNode, WGPeer } from '@/lib/hubClient';
import type { Terminal } from '@/types/fleet';

const KIND_ICON: Record<string, string> = {
  dog: 'ri-android-line', glasses: 'ri-glasses-line', phone: 'ri-smartphone-line',
  drone: 'ri-flight-takeoff-line', vehicle: 'ri-car-line', other: 'ri-device-line',
  unregistered: 'ri-server-line', person: 'ri-computer-line',
};
const KIND_LABEL: Record<string, string> = {
  dog: '机器狗', glasses: 'AI眼镜', phone: '手机', drone: '无人机',
  vehicle: '无人车', other: '终端', unregistered: '未登记(不在设备注册表)', person: '人的电脑',
};

/** 一个画在图上的点。tailnet 节点与 wg 对端共用这个形状,靠 via 区分。 */
interface Dot {
  id: string;
  name: string;
  sub: string;          // 副标题:overlay IP
  kind: string;
  online: boolean;
  lastSeen: string;     // 已格式化的「x分钟前」
  via: 'tailnet' | 'wg';
  ips: string[];
  deviceId?: string;
}

function dotsFrom(nodes: HubNode[], peers: WGPeer[] | undefined, terminals: Terminal[]): Dot[] {
  const byId = new Map(terminals.map((t) => [t.id, t]));
  const nodeDots: Dot[] = nodes.map((n) => {
    // deviceId 由 headscale 的 tag 反解, 没打 tag 时为空 —— 退一步用节点名匹配
    // 注册表(headscale 的 hostname 就是入网时用的设备 id)。
    const dev = (n.deviceId ? byId.get(n.deviceId) : undefined) ?? byId.get(n.name);
    return {
      id: `n:${n.id}`,
      // 注册表里有中文名就用中文名 —— dog-01 是标识, 小智才是人叫得出的名字
      name: dev?.name || n.name,
      sub: n.ips?.[0] ?? NA,
      // 入网了却不在设备注册表里 = 基础设施, 或者漏登记的机器。这是两个数据源
      // 对不上的地方, 值得画出来, 而不是笼统显示成一台「终端」。
      kind: dev?.type ?? 'unregistered',
      online: n.online,
      lastSeen: timeAgo(n.lastSeen),
      via: 'tailnet',
      ips: n.ips ?? [],
      deviceId: n.deviceId,
    };
  });
  const peerDots: Dot[] = (peers ?? []).map((p) => ({
    id: `w:${p.address}`,
    name: p.address,
    sub: p.endpoint || NA,
    kind: 'person',
    online: p.online,
    lastSeen: timeAgoSec(p.lastSeen),
    via: 'wg',
    ips: [p.address],
  }));
  return [...nodeDots, ...peerDots];
}

// ─── KPI ─────────────────────────────────────────────────────────────────

function Kpi({ value, label, hint, tone, icon }: {
  value: string; label: string; hint?: string; tone?: string; icon: string;
}) {
  return (
    <div className="bg-background-50 border border-background-200/70 rounded-xl p-3 card-lift">
      <div className="flex items-center gap-1.5 mb-1.5">
        <i className={`${icon} text-xs text-foreground-400`} />
        <span className="text-[10px] text-foreground-500">{label}</span>
      </div>
      <div className={`text-xl font-bold font-mono leading-none ${tone ?? 'text-foreground-800'}`}>{value}</div>
      {hint && <div className="text-[10px] text-foreground-400 mt-1.5 truncate">{hint}</div>}
    </div>
  );
}

// ─── 拓扑 ─────────────────────────────────────────────────────────────────

function Topology({ dots, selected, onSelect }: {
  dots: Dot[]; selected: string | null; onSelect: (id: string | null) => void;
}) {
  const n = dots.length;
  // 几何三条:
  //  · 画布高度随节点数收缩 —— 两个节点不该撑出一屏空白(旧版固定 460);
  //  · 半径由"hub 半径 + 节点半径 + 标签两行"倒推, 而不是拍一个比例 ——
  //    拍比例的后果是上一版:节点圆压在 HUB 上、IP 文字互相叠;
  //  · n=2 时排成水平(起始角 0°/180°)。均分整圈遇到两个点会给出正上/正下,
  //    那正是最早那张"一条竖线"的截图。
  const W = 680;
  const H = n <= 2 ? 300 : n <= 5 ? 360 : 440;
  const cx = W / 2, cy = H / 2;
  const HUB_R = 34, NODE_R = 23, LABEL_H = 32;
  const R = Math.min(
    Math.max(HUB_R + NODE_R + 62, 130),      // 下限:圆不许贴上 HUB
    Math.min(W / 2, H / 2) - NODE_R - LABEL_H, // 上限:标签不许出画布
  );

  const placed = useMemo(() => dots.map((d, i) => {
    const a = n === 1 ? 0
      : n === 2 ? (i === 0 ? Math.PI : 0)
        : (i / n) * Math.PI * 2 - Math.PI / 2;
    return { d, x: cx + R * Math.cos(a), y: cy + R * Math.sin(a) };
  }), [dots, n, cx, cy, R]);

  if (n === 0) {
    return <NoDataSource title="私有网里还没有节点"
      reason="设备用自己的令牌调 POST /net/enroll 入网后会出现在这里。控制面可达但一台设备都没加入,不是故障。" />;
  }

  return (
    <div className="w-full overflow-x-auto">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto min-w-[440px]" role="img" aria-label="私有网拓扑">
        <defs>
          <radialGradient id="hubGlow">
            <stop offset="0%" stopColor="rgb(99 102 241 / 0.18)" />
            <stop offset="100%" stopColor="rgb(99 102 241 / 0)" />
          </radialGradient>
        </defs>
        <circle cx={cx} cy={cy} r={70} fill="url(#hubGlow)" />

        {/* 曲线边:radial 布局用贝塞尔比直线更能体现"以 hub 为中心"(yFiles 规范) */}
        {placed.map(({ d, x, y }) => {
          const mx = (cx + x) / 2 + (y - cy) * 0.12;
          const my = (cy + y) / 2 - (x - cx) * 0.12;
          const dim = !d.online;
          return (
            <path key={`e-${d.id}`} d={`M ${cx} ${cy} Q ${mx} ${my} ${x} ${y}`} fill="none"
              stroke={dim ? 'rgb(161 161 170 / 0.35)' : d.via === 'wg' ? 'rgb(217 162 78 / 0.55)' : 'rgb(99 102 241 / 0.5)'}
              strokeWidth={selected === d.id ? 2.5 : 1.5}
              strokeDasharray={d.via === 'wg' ? '5 4' : dim ? '3 5' : undefined} />
          );
        })}

        <g>
          <circle cx={cx} cy={cy} r={34} fill="rgb(238 242 255)" stroke="rgb(99 102 241)" strokeWidth={2} />
          <text x={cx} y={cy - 2} textAnchor="middle" style={{ fontSize: 12, fontWeight: 700 }} fill="rgb(67 56 202)">HUB</text>
          <text x={cx} y={cy + 11} textAnchor="middle" style={{ fontSize: 8 }} fill="rgb(99 102 241)">控制面</text>
        </g>

        {placed.map(({ d, x, y }) => {
          const sel = selected === d.id;
          const r = sel ? 27 : 23;
          const ring = !d.online ? 'rgb(161 161 170)' : d.via === 'wg' ? 'rgb(217 162 78)' : 'rgb(34 160 108)';
          const fill = !d.online ? 'rgb(244 244 245)' : d.via === 'wg' ? 'rgb(254 249 240)' : 'rgb(240 253 246)';
          return (
            <g key={d.id} onClick={() => onSelect(sel ? null : d.id)} style={{ cursor: 'pointer' }}
              role="button" aria-label={`${d.name} ${d.online ? '在线' : '离线'}`}>
              {sel && <circle cx={x} cy={y} r={r + 6} fill="none" stroke={ring} strokeWidth={1} opacity={0.35} />}
              <circle cx={x} cy={y} r={r} fill={fill} stroke={ring} strokeWidth={sel ? 3 : 2} />
              {/* 圆里放设备类型图标, 不是 IP 末位数字 —— 一眼看出这是狗还是眼镜 */}
              <text x={x} y={y + 6} textAnchor="middle" style={{ fontSize: 17, fontFamily: 'remixicon' }}
                fill={d.online ? 'rgb(63 63 70)' : 'rgb(161 161 170)'}
                dangerouslySetInnerHTML={{ __html: iconGlyph(d.kind) }} />
              <text x={x} y={y + r + 15} textAnchor="middle" style={{ fontSize: 11, fontWeight: 600 }}
                fill={d.online ? 'rgb(63 63 70)' : 'rgb(161 161 170)'}>
                {d.name.length > 12 ? d.name.slice(0, 11) + '…' : d.name}
              </text>
              <text x={x} y={y + r + 28} textAnchor="middle" style={{ fontSize: 9, fontFamily: 'ui-monospace, monospace' }}
                fill="rgb(161 161 170)">{d.sub.length > 18 ? d.sub.slice(0, 17) + '…' : d.sub}</text>
              {!d.online && (
                <text x={x} y={y - r - 6} textAnchor="middle" style={{ fontSize: 9 }} fill="rgb(161 161 170)">离线</text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

/**
 * remixicon 的字形码点。SVG <text> 里塞不了 <i class="ri-…">,只能填 PUA 码点。
 * **码点是从 node_modules/remixicon/fonts/remixicon.css 里抄的,不是猜的** ——
 * 上一版凭印象写,六个图标全画成了同一个云朵形状(字体里那个位置恰好有别的字形,
 * 于是"错得很像对的":有图案、不报错、就是不对)。改字体版本时重新对一遍这张表。
 */
const GLYPH: Record<string, string> = {
  dog: '&#xea36;',       // ri-android-line
  glasses: '&#xf40e;',   // ri-glasses-line
  phone: '&#xf15a;',     // ri-smartphone-line
  drone: '&#xed43;',     // ri-flight-takeoff-line
  vehicle: '&#xeb39;',   // ri-car-line
  person: '&#xebca;',    // ri-computer-line
  other: '&#xec2e;',        // ri-device-line
  unregistered: '&#xf0e0;', // ri-server-line
};
function iconGlyph(kind: string): string {
  return GLYPH[kind] ?? GLYPH.other;
}

// ─── 列表 ─────────────────────────────────────────────────────────────────

function NodeList({ dots, selected, onSelect }: {
  dots: Dot[]; selected: string | null; onSelect: (id: string | null) => void;
}) {
  if (dots.length === 0) {
    return <NoDataSource title="私有网里还没有节点" reason="设备调 POST /net/enroll 入网后出现在这里。" />;
  }
  return (
    <div className="divide-y divide-background-200/60">
      {dots.map((d) => (
        <button key={d.id} onClick={() => onSelect(selected === d.id ? null : d.id)}
          className={`w-full text-left px-3 py-3 flex items-center gap-3 transition-colors cursor-pointer ${selected === d.id ? 'bg-primary-50/60' : 'hover:bg-background-100'}`}>
          <span className={`w-9 h-9 shrink-0 rounded-lg flex items-center justify-center ${d.online ? (d.via === 'wg' ? 'bg-amber-50 text-amber-600' : 'bg-accent-50 text-accent-600') : 'bg-background-100 text-foreground-300'}`}>
            <i className={`${KIND_ICON[d.kind] ?? KIND_ICON.other} text-base`} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className={`text-sm font-medium truncate ${d.online ? 'text-foreground-800' : 'text-foreground-400'}`}>{d.name}</span>
              <span className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded-full ${d.online ? 'bg-accent-50 text-accent-700' : 'bg-background-100 text-foreground-400'}`}>
                {d.online ? '在线' : '离线'}
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-x-2.5 gap-y-0.5 mt-0.5 text-[11px] text-foreground-500">
              <span>{KIND_LABEL[d.kind] ?? '终端'}</span>
              <span className="font-mono truncate">{d.sub}</span>
              <span className="text-foreground-400">{d.via === 'wg' ? 'wg 网关' : 'tailnet'}</span>
            </div>
          </div>
          <span className="shrink-0 text-[11px] font-mono text-foreground-400">{d.lastSeen}</span>
        </button>
      ))}
    </div>
  );
}

// ─── 页面 ─────────────────────────────────────────────────────────────────

export default function NetworkPage() {
  const { view, loading, error, unavailable, refetch } = useNetwork();
  const { terminals } = useTerminals();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [tab, setTab] = useState<'graph' | 'list'>('graph');
  const [whyNoLatency, setWhyNoLatency] = useState(false);

  // 用 useMemo 稳住引用:`view?.nodes ?? []` 每次渲染都是新数组, 会让下面两个
  // useMemo 每帧重算(拓扑坐标每帧抖一下)。
  const nodes = useMemo(() => view?.nodes ?? [], [view?.nodes]);
  const dots = useMemo(() => dotsFrom(nodes, view?.wgPeers, terminals), [nodes, view?.wgPeers, terminals]);
  const selected = dots.find((d) => d.id === selectedId) ?? null;
  const online = nodes.filter((n) => n.online).length;
  const wgOnline = view?.wgPeers?.filter((p) => p.online).length;

  // 「最久未上线」:一队里掉得最久的那台。比"平均延迟"这种没有源的数有用得多,
  // 而且它是**数出来的** —— 全在线时显示"全部在线"而不是编一个 0。
  const stalest = useMemo(() => {
    const offline = nodes.filter((n) => !n.online);
    if (nodes.length === 0) return { value: NA, hint: '尚无入网节点' };
    if (offline.length === 0) return { value: '全部在线', hint: `${nodes.length} 台设备都在私有网里` };
    const worst = offline.reduce((a, b) =>
      (Date.parse(a.lastSeen ?? '') || 0) < (Date.parse(b.lastSeen ?? '') || 0) ? a : b);
    return { value: timeAgo(worst.lastSeen), hint: `${worst.name} 掉队最久(共 ${offline.length} 台离线)` };
  }, [nodes]);

  return (
    <div className="p-4 lg:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4 animate-fade-in-up">
        <div>
          <h1 className="text-lg font-bold text-foreground-900 font-heading">私有网</h1>
          <p className="text-xs text-foreground-500 mt-0.5">谁入网了 · 此刻谁在线 · 从哪儿接进来的 —— 数据来自 hub 的 /net/health 与 /net/nodes</p>
        </div>
        <button onClick={refetch} className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border border-background-200/70 text-foreground-500 hover:bg-background-100 cursor-pointer whitespace-nowrap">
          <i className={`ri-refresh-line text-sm ${loading ? 'animate-spin' : ''}`} />刷新
        </button>
      </div>

      {error && <div className="mb-4"><LoadError error={error} onRetry={refetch} /></div>}

      {unavailable ? (
        <div className="bg-background-50 border border-background-200/70 rounded-xl">
          <NoDataSource title="私有网未配置" reason={unavailable} phase="配置 hub 的 headscale 地址与 API key 后本页自动变真" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 lg:gap-3 mb-3 animate-fade-in-up" style={{ animationDelay: '0.06s' }}>
            <Kpi icon="ri-node-tree" label="在线节点 / 已入网"
              value={loading && !view ? '…' : `${online}/${nodes.length}`}
              tone={nodes.length > 0 && online === nodes.length ? 'text-accent-600' : undefined}
              hint={nodes.length ? `${nodes.length - online} 台离线` : '尚无设备入网'} />
            <Kpi icon="ri-broadcast-line" label="控制面 headscale"
              value={view?.health ? (view.health.ok ? '可达' : '不可达') : NA}
              tone={view?.health?.ok ? 'text-accent-600' : 'text-amber-600'}
              hint={view?.health?.ok ? '新设备可加入' : '已建连接不受影响,仅新设备无法加入'} />
            <Kpi icon="ri-computer-line" label="wg 对端(人的电脑)"
              value={view?.wgError ? '读失败' : view?.wgPeers === undefined ? '未启用' : String(wgOnline ?? 0)}
              tone={view?.wgError ? 'text-red-500' : undefined}
              hint={view?.wgError ? view.wgError
                : view?.wgPeers === undefined ? '本机没配 wg 网关'
                  : `共 ${view.wgPeers.length} 个已配对端`} />
            <Kpi icon="ri-history-line" label="最久未上线" value={stalest.value}
              tone={stalest.value === '全部在线' ? 'text-accent-600' : undefined} hint={stalest.hint} />
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-3">
            <div className="inline-flex p-0.5 rounded-lg bg-background-100">
              {([['graph', '拓扑', 'ri-node-tree'], ['list', '列表', 'ri-list-check']] as const).map(([k, label, icon]) => (
                <button key={k} onClick={() => setTab(k)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium cursor-pointer whitespace-nowrap transition-colors ${tab === k ? 'bg-background-50 text-foreground-800 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}>
                  <i className={`${icon} text-sm`} />{label}
                </button>
              ))}
            </div>
            <span className="text-[11px] text-foreground-400">共 {dots.length} 个接入点(tailnet 节点 + wg 对端)</span>
          </div>

          <div className="flex flex-col lg:flex-row lg:items-start gap-3 lg:gap-4 animate-fade-in-up" style={{ animationDelay: '0.12s' }}>
            <div className="w-full lg:flex-1 bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
              {loading && !view ? (
                <div className="h-[280px] flex items-center justify-center text-xs text-foreground-400">
                  <i className="ri-loader-4-line animate-spin mr-2" />读取中…
                </div>
              ) : tab === 'graph' ? (
                <div className="p-2"><Topology dots={dots} selected={selectedId} onSelect={setSelectedId} /></div>
              ) : (
                <NodeList dots={dots} selected={selectedId} onSelect={setSelectedId} />
              )}
            </div>

            <div className="w-full lg:w-[330px] flex-shrink-0 space-y-3">
              <div className="bg-background-50 border border-background-200/70 rounded-xl p-3">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs font-bold text-foreground-800">{selected ? '节点详情' : '接入概览'}</h3>
                  {selected && (
                    <button onClick={() => setSelectedId(null)} className="text-[11px] text-foreground-400 hover:text-foreground-600 cursor-pointer">取消选择</button>
                  )}
                </div>

                {selected ? (
                  <div className="space-y-2 text-xs">
                    <div className="flex items-center gap-2 pb-2 border-b border-background-200/60">
                      <span className={`w-9 h-9 rounded-lg flex items-center justify-center ${selected.online ? 'bg-accent-50 text-accent-600' : 'bg-background-100 text-foreground-300'}`}>
                        <i className={`${KIND_ICON[selected.kind] ?? KIND_ICON.other} text-base`} />
                      </span>
                      <div className="min-w-0">
                        <div className="font-medium text-foreground-800 truncate">{selected.name}</div>
                        <div className="text-[11px] text-foreground-400">{KIND_LABEL[selected.kind] ?? '终端'} · {selected.via === 'wg' ? 'wg 网关接入' : 'tailnet 节点'}</div>
                      </div>
                    </div>
                    <Row k="状态" v={selected.online ? '在线' : '离线'} tone={selected.online ? 'text-accent-600' : 'text-foreground-400'} />
                    <Row k="最后在线" v={selected.lastSeen} mono />
                    {selected.deviceId && <Row k="设备标识" v={selected.deviceId} mono />}
                    <div>
                      <span className="text-foreground-400">地址</span>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {selected.ips.map((ip) => (
                          <button key={ip} onClick={() => navigator.clipboard?.writeText(ip)} title="点击复制"
                            className="px-1.5 py-0.5 rounded bg-background-100 hover:bg-background-200 font-mono text-[11px] text-foreground-600 cursor-pointer">
                            {ip}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  // 没选中时给概览, 而不是一句"请点击" —— 右栏不该有一半时间是空的
                  <div className="space-y-2">
                    <Bar label="tailnet 节点" online={nodes.filter((n) => n.online).length} total={nodes.length} tone="bg-accent-500" />
                    <Bar label="wg 对端" online={wgOnline ?? 0} total={view?.wgPeers?.length ?? 0} tone="bg-amber-500" />
                    <p className="text-[11px] text-foreground-400 pt-1 leading-relaxed">
                      点{tab === 'graph' ? '图上的节点' : '列表里的一行'}看详情。手机上点一下即可,不依赖悬停。
                    </p>
                  </div>
                )}
              </div>

              <div className="bg-background-50 border border-background-200/70 rounded-xl p-3">
                <h3 className="text-xs font-bold text-foreground-800 mb-2">图例</h3>
                <ul className="space-y-1.5 text-[11px] text-foreground-500">
                  <Legend swatch={<span className="w-3 h-3 rounded-full border-2 border-indigo-500 bg-indigo-50" />} text="HUB(控制面 headscale)" />
                  <Legend swatch={<span className="w-3 h-3 rounded-full border-2 border-emerald-500 bg-emerald-50" />} text="在线的 tailnet 节点" />
                  <Legend swatch={<span className="w-3 h-3 rounded-full border-2 border-amber-500 bg-amber-50" />} text="wg 对端(人的电脑)" />
                  <Legend swatch={<span className="w-3 h-3 rounded-full border-2 border-background-300 bg-background-100" />} text="离线" />
                  <Legend swatch={<span className="w-6 h-0 border-t-2 border-indigo-400" />} text="注册关系 —— 不是实测链路" />
                </ul>
              </div>

              {/* 无源那条从 KPI 位降级成脚注:该说,但不值得占最显眼的格子 */}
              <button onClick={() => setWhyNoLatency(!whyNoLatency)}
                className="w-full text-left bg-background-50 border border-background-200/70 rounded-xl p-3 cursor-pointer hover:bg-background-100/60 transition-colors">
                <div className="flex items-center gap-2">
                  <i className="ri-information-line text-sm text-foreground-400" />
                  <span className="text-[11px] text-foreground-600 flex-1">为什么没有延迟/丢包/带宽?</span>
                  <i className={`ri-arrow-down-s-line text-sm text-foreground-400 transition-transform ${whyNoLatency ? 'rotate-180' : ''}`} />
                </div>
                {whyNoLatency && (
                  <p className="mt-2 text-[11px] text-foreground-500 leading-relaxed">
                    这些量必须由链路**两端自己测**并上报;控制面只知道谁在线。编一个「平均延迟」
                    是最容易骗过自己的那种假数字。等 P2 的设备遥测上报接通后,这里才会有真值。
                  </p>
                )}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Row({ k, v, mono, tone }: { k: string; v: string; mono?: boolean; tone?: string }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-foreground-400 shrink-0">{k}</span>
      <span className={`text-right break-all ${mono ? 'font-mono' : ''} ${tone ?? 'text-foreground-700'}`}>{v}</span>
    </div>
  );
}

function Bar({ label, online, total, tone }: { label: string; online: number; total: number; tone: string }) {
  const pctW = total > 0 ? (online / total) * 100 : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-[11px] mb-1">
        <span className="text-foreground-500">{label}</span>
        <span className="font-mono text-foreground-700">{total === 0 ? NA : `${online}/${total}`}</span>
      </div>
      <div className="h-1.5 rounded-full bg-background-200 overflow-hidden">
        <div className={`h-full rounded-full ${tone}`} style={{ width: `${pctW}%` }} />
      </div>
    </div>
  );
}

function Legend({ swatch, text }: { swatch: React.ReactNode; text: string }) {
  return <li className="flex items-center gap-2">{swatch}<span>{text}</span></li>;
}
